'use client';

import { ApolloCache } from '@apollo/client';
import { useTranslations } from 'next-intl';
import { FC, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { PUBLICATION_FILE, UNPUBLICATION_FILE } from '@/.graphql';
import EyeOffIcon from '@/public/icons/eye_off.svg';
import EyeIcon from '@/public/icons/eye.svg';
import ReusedIcon from '@/public/icons/reused.svg';
import { SerializedFile } from '@/src/utils/serializeFile';
import { useMutation } from '@apollo/client/react';

// ========== TYPES ==========
interface PublishBlockProps {
  publishedInFeedText: string;
  publishInFeedText: string;
  publishedText: string;
  reusedText: string;
  nonPublishedText: string;
  publishInfoText: string;
  activeFile: SerializedFile;
  isAllowPublish: boolean;
  isAdmin: boolean;
}

interface UsePublishToggleProps {
  activeFile: SerializedFile;
}

interface UsePublishToggleReturn {
  isPublished: boolean;
  isLoading: boolean;
  handleTogglePublish: () => Promise<void>;
}

// ========== CONSTANTS ==========
const STYLES = {
  publishBlock: 'mb-4 p-4 rounded-xl bg-fill-zinc900',
  publishText: 'text-zinc400',
  publishHeader: 'text-sm text-fill-zinc400 mb-2 opacity-50',
  publishContent: 'flex items-center justify-between mb-1',
  publishLeft: 'flex items-center gap-3',
  publishStatus: 'flex items-center gap-2 text-base font-medium',
  publishToggle: [
    'relative w-12 h-6 rounded-full transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-green-500',
    'focus:ring-offset-2 focus:ring-offset-zinc-900',
  ].join(' '),
  publishReusedTimes: 'text-sm text-fill-zinc400 opacity-50 font-normal',
  publishToggleThumb: [
    'absolute top-0.5 w-5 h-5 bg-white rounded-full',
    'transition-transform duration-200 shadow-sm',
  ].join(' '),
  helpIcon: [
    'w-4 h-4 flex items-center justify-center bg-fill-white opacity-50 text-zinc900 rounded-full cursor-pointer',
    'hover:text-fill-zinc300 transition-colors',
  ].join(' '),
  helpBanner: [
    'fixed z-[10] bottom-5 left-3 right-3 bg-fill-zinc900 rounded-xl',
    'min-h-[100px] max-w-[458px] m-auto p-4 flex items-start gap-2',
  ].join(' '),
  eyeIcon: 'w-6 h-6',
  errorMessage: [
    'mt-2 text-sm text-red-400 bg-red-900/20',
    'p-2 rounded border border-red-800/30',
  ].join(' '),
} as const;

const TOGGLE_STATES = {
  published: {
    colors: 'bg-green-500 hover:bg-green-600',
    thumbPosition: 'translate-x-6',
    textColor: 'text-green-500',
    iconColor: 'text-green-500',
  },
  unpublished: {
    colors: 'bg-zinc-800 hover:bg-zinc-700',
    thumbPosition: 'translate-x-0.5',
    textColor: 'text-white',
    iconColor: 'text-fill-zinc400',
  },
  disabled: 'opacity-50 cursor-not-allowed',
} as const;

// ========== CACHE UTILITIES ==========
const updateImageGenerationCache = (
  cache: ApolloCache,
  activeFile: SerializedFile,
  fileId: string,
  isPublishing: boolean,
) => {
  if ('imageGenerationId' in activeFile) {
    cache.modify({
      id: `ImageGeneration:${activeFile.imageGenerationId}`,
      fields: {
        publishedFileIds(existing = []) {
          return isPublishing
            ? [...existing, fileId]
            : existing.filter((id: string) => id !== fileId);
        },
      },
    });
  }
};

const updatePublicationsCache = (cache: ApolloCache, fileId: string) => {
  cache.modify({
    fields: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getPublications(existing: any, { readField }) {
        if (!existing) {
          return existing;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const currentData = readField('data', existing) as any[];

        return {
          ...existing,
          data:
            currentData?.filter((pub) => readField('fileId', pub) !== fileId) ||
            [],
        };
      },
    },
  });
};

// ========== CUSTOM HOOK ==========
const usePublishToggle = ({
  activeFile,
}: UsePublishToggleProps): UsePublishToggleReturn => {
  const [isPublished, setPublished] = useState(activeFile.isPublished);

  const { fileId } = activeFile;

  const [publishFile, { loading: publishLoading }] =
    useMutation(PUBLICATION_FILE);

  const [unpublishFile, { loading: unpublishLoading }] =
    useMutation(UNPUBLICATION_FILE);

  const handlePublish = useCallback(async () => {
    if (!fileId) {
      throw new Error('File ID is required for publishing');
    }

    await publishFile({
      variables: { fileId },
      update: (cache) => {
        updateImageGenerationCache(cache, activeFile, fileId, true);
      },
    });
  }, [publishFile, fileId, activeFile]);

  const handleUnpublish = useCallback(async () => {
    if (!fileId) {
      throw new Error('File ID is required for unpublishing');
    }

    await unpublishFile({
      variables: { fileId },
      update: (cache) => {
        updateImageGenerationCache(cache, activeFile, fileId, false);
        updatePublicationsCache(cache, fileId);
      },
    });
  }, [unpublishFile, fileId, activeFile]);

  const handleTogglePublish = useCallback(async () => {
    try {
      if (isPublished) {
        await handleUnpublish();
      } else {
        await handlePublish();
      }

      setPublished((prev) => !prev);
    } catch (error) {
      console.error('Error toggling publish status:', error);
    }
  }, [isPublished, handlePublish, handleUnpublish]);

  const isLoading = publishLoading || unpublishLoading;

  return {
    isPublished,
    isLoading,
    handleTogglePublish,
  };
};

// ========== COMPONENT UTILITIES ==========
const getToggleClasses = (isPublished: boolean, isLoading: boolean) => {
  const baseClasses = STYLES.publishToggle;
  const stateClasses = isPublished
    ? TOGGLE_STATES.published.colors
    : TOGGLE_STATES.unpublished.colors;
  const disabledClasses = isLoading ? TOGGLE_STATES.disabled : '';

  return twMerge(baseClasses, stateClasses, disabledClasses);
};

const getThumbClasses = (isPublished: boolean) => {
  const baseClasses = STYLES.publishToggleThumb;
  const positionClasses = isPublished
    ? TOGGLE_STATES.published.thumbPosition
    : TOGGLE_STATES.unpublished.thumbPosition;

  return twMerge(baseClasses, positionClasses);
};

const getStatusTextClasses = (isPublished: boolean) =>
  isPublished
    ? TOGGLE_STATES.published.textColor
    : TOGGLE_STATES.unpublished.textColor;

const getEyeIconClasses = (isPublished: boolean) => {
  const baseClasses = STYLES.eyeIcon;
  const colorClasses = isPublished
    ? TOGGLE_STATES.published.iconColor
    : TOGGLE_STATES.unpublished.iconColor;

  return twMerge(baseClasses, colorClasses);
};

// ========== MAIN COMPONENT ==========
export const PublishBlock: FC<PublishBlockProps> = ({
  activeFile,
  publishedInFeedText,
  publishInFeedText,
  publishedText,
  nonPublishedText,
  publishInfoText,
  reusedText,
  isAllowPublish,
  isAdmin,
}) => {
  const [isShowHelpBanner, setIsShowHelpBanner] = useState(false);
  const t = useTranslations('ProfilePage');

  const handleHelpToggle = useCallback(() => {
    setIsShowHelpBanner(!isShowHelpBanner);
  }, [isShowHelpBanner]);

  const handleHelpMouseEnter = () => {
    setIsShowHelpBanner(true);
  };

  const handleHelpMouseLeave = () => {
    setIsShowHelpBanner(false);
  };

  const {
    isPublished,
    isLoading,
    handleTogglePublish: togglePublish,
  } = usePublishToggle({
    activeFile,
  });

  const handleToggleClick = useCallback(() => {
    if (!isLoading) {
      togglePublish();
    }
  }, [togglePublish, isLoading]);

  return (
    <div
      className={STYLES.publishBlock}
      role="region"
      aria-label="Publication settings"
    >
      {isAllowPublish ? (
        <>
          <div className={STYLES.publishHeader}>
            {isPublished ? publishedInFeedText : publishInFeedText}
          </div>
          <div>
            <div className={STYLES.publishContent}>
              <div className={STYLES.publishLeft}>
                {isPublished ? (
                  <EyeIcon
                    className={getEyeIconClasses(isPublished)}
                    aria-hidden="true"
                  />
                ) : (
                  <EyeOffIcon
                    className={getEyeIconClasses(isPublished)}
                    aria-hidden="true"
                  />
                )}

                <div className={STYLES.publishStatus}>
                  <span className={getStatusTextClasses(isPublished)}>
                    {isPublished ? publishedText : nonPublishedText}
                  </span>
                  <button
                    type="button"
                    className={STYLES.helpIcon}
                    onClick={handleHelpToggle}
                    onMouseEnter={handleHelpMouseEnter}
                    onMouseLeave={handleHelpMouseLeave}
                    aria-label="More information about publishing"
                    title="Learn more about publishing"
                  >
                    ?
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleToggleClick}
                disabled={isLoading}
                className={getToggleClasses(isPublished, isLoading)}
              >
                <div
                  className={getThumbClasses(isPublished)}
                  aria-hidden="true"
                />
                <span className="sr-only">
                  {isLoading
                    ? 'Processing...'
                    : `Currently ${isPublished ? 'published' : 'unpublished'}`}
                </span>
              </button>
            </div>
            {isPublished && (
              <div className={STYLES.publishStatus}>
                <ReusedIcon />
                <div>{reusedText}</div>
                <div className={STYLES.publishReusedTimes}>
                  {t('time', {
                    times: activeFile.reuseCount ?? 0,
                  })}
                </div>
              </div>
            )}
          </div>
          {isShowHelpBanner && (
            <div className={STYLES.helpBanner}>
              <div
                className={twMerge(
                  STYLES.helpIcon,
                  'opacity-100 shrink-0 mt-1',
                )}
              >
                ?
              </div>
              <div>{publishInfoText}</div>
            </div>
          )}
        </>
      ) : (
        <div className={STYLES.publishText}>{publishInfoText}</div>
      )}
    </div>
  );
};
